import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, getForm } from '../api';
import { GapsPanel } from '../components/GapsPanel';
import { KBDrawer } from '../components/KBDrawer';
import { ModifyReviewStatusDialog } from '../components/ModifyReviewStatusDialog';
import { ReviewBadges } from '../components/ReviewBadges';
import { ApprovalsTimeline } from '../components/FormSections/ApprovalsTimeline';
import { AssignmentGroup } from '../components/FormSections/AssignmentGroup';
import { CountryAvailability } from '../components/FormSections/CountryAvailability';
import { FieldsTable } from '../components/FormSections/FieldsTable';
import { FormHero } from '../components/FormSections/FormHero';
import { FormIdentitySection } from '../components/FormSections/FormIdentitySection';
import { SLATable } from '../components/FormSections/SLATable';
import { Warnings } from '../components/FormSections/Warnings';
import { useTestTicketUrl } from '../state/config';
import { useGeoFilter } from '../state/geoFilter';
import { useReviews } from '../state/reviews';
import type { FormView } from '../types';

/**
 * Form-detail page (spec §3 step 4 + §9.1).
 *
 * Hero banner sets context (Core Model + stream pills, title, description,
 * action buttons). Below: a stack of collapsible sections — Form Identity,
 * Variables/Fields, Workflow & Approvals, Assignment Group, SLA, Company
 * Codes — modelled on the original standalone app's design-doc layout.
 *
 * Right rail keeps the review/gap workflow (per-geography badges, GapsPanel).
 */
export function FormScreen() {
  const params = useParams<{ streamId: string; kbNumber?: string; sysId?: string }>();
  const stream = params.streamId ?? '';
  // /streams/:s/form/sys/:sysId  → identifier = "sys/<sys_id>"
  // /streams/:s/form/:kbNumber  → identifier = "KB..."
  const identifier = params.sysId ? `sys/${params.sysId}` : (params.kbNumber ?? '');

  const { geo } = useGeoFilter(undefined);
  const [view, setView] = useState<FormView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setView(null);
    getForm(identifier, geo)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch((e) => {
        if (!cancelled) {
          if (e instanceof ApiError && e.status === 404) {
            setError('This form is not yet in the local snapshot — run a sync first.');
          } else {
            setError(e instanceof Error ? e.message : String(e));
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [identifier, geo]);

  const kbNumber = view?.identity.kb_number ?? null;
  const producerSysId = view?.identity.producer_sys_id ?? null;

  const reviewKb = kbNumber ?? identifier;
  const { byKb: reviewsByKb, refresh: refreshReviews } = useReviews(
    useMemo(() => ({ kb_number: reviewKb }), [reviewKb]),
  );
  const reviewsByGeo = reviewsByKb.get(reviewKb);

  const testUrl = useTestTicketUrl(producerSysId);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const formName = view?.identity.name || view?.identity.short_description || null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <nav className="text-sm mb-3">
        <Link
          to={`/streams/${encodeURIComponent(stream)}`}
          className="text-brand-danone hover:underline"
        >
          ← {stream}
        </Link>
      </nav>

      {loading && !view && <div className="text-slate-500">Loading form…</div>}

      {error && !view && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {view && (
        <>
          <FormHero
            identity={view.identity}
            actions={
              <>
                {testUrl && (
                  <a
                    href={testUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary h-9"
                  >
                    See request in Founda ↗
                  </a>
                )}
                {view.identity.has_kb_article && kbNumber && (
                  <button
                    type="button"
                    className="btn-secondary h-9 inline-flex items-center justify-center gap-1.5"
                    onClick={() => setDrawerOpen(true)}
                  >
                    <OpenSidebarIcon />
                    <span>Open full documentation</span>
                  </button>
                )}
              </>
            }
          />

          <div className="-mt-2 mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
            <ReviewBadges
              kbNumber={reviewKb}
              reviewsByGeo={reviewsByGeo}
              topBarGeo={geo}
              stream={stream}
              formSysId={producerSysId}
              onChanged={refreshReviews}
              size="md"
            />
            <button
              type="button"
              className="btn-secondary h-9"
              onClick={() => setReviewOpen(true)}
            >
              Mark reviewed…
            </button>
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-4">
              <FormIdentitySection identity={view.identity} />
              <FieldsTable
                fields={view.fields}
                hasConditionalLogic={view.has_conditional_logic}
                testTicketUrl={testUrl}
                onOpenKb={
                  view.identity.has_kb_article && kbNumber
                    ? () => setDrawerOpen(true)
                    : undefined
                }
              />
              <ApprovalsTimeline steps={view.approvals} />
              <AssignmentGroup group={view.assignment_group} />
              <SLATable slas={view.slas} />
              <CountryAvailability countries={view.countries} />
              <Warnings messages={view.warnings} />
            </div>

            <aside className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
              <GapsPanel
                stream={stream}
                kbNumber={reviewKb}
                formSysId={producerSysId}
                formName={formName}
                topBarGeo={geo}
              />
            </aside>
          </div>
        </>
      )}

      {reviewOpen && view && (
        <ModifyReviewStatusDialog
          stream={stream}
          kbNumber={reviewKb}
          formSysId={producerSysId}
          formName={formName}
          topBarGeo={geo}
          reviewsByGeo={reviewsByGeo}
          onClose={() => setReviewOpen(false)}
          onSaved={() => {
            setReviewOpen(false);
            refreshReviews();
          }}
        />
      )}

      {drawerOpen && kbNumber && (
        <KBDrawer kbNumber={kbNumber} onClose={() => setDrawerOpen(false)} />
      )}
    </div>
  );
}

function OpenSidebarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="14" height="12" rx="2" />
      <line x1="13" y1="4" x2="13" y2="16" />
      <path d="M7 8l2 2-2 2" />
    </svg>
  );
}
